import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Main from './Main';
import { useLoginModal } from '../Context/LoginModalContext';

function getModalRoot() {
  return document.body;
}

export default function LoginModal() {
  const { isOpen, redirectPath, closeLogin } = useLoginModal();
  const navigate = useNavigate();

  // Cancel = dismiss modal AND go home. Distinct from closeLogin (which is called after successful auth).
  const handleCancel = useCallback(() => {
    closeLogin();
    navigate('/');
  }, [closeLogin, navigate]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleCancel();
    };

    document.addEventListener('keydown', onKeyDown);

    // Prevent background scroll while modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, handleCancel]);

  if (!isOpen) return null;

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
      onMouseDown={(e) => {
        // Close when clicking backdrop (not when clicking inside card)
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 520,
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-elevation)',
          padding: 24,
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={handleCancel}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: 999,
            border: '1px solid var(--border)',
            background: 'var(--background)',
            color: 'var(--muted-foreground)',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>

        <Main variant="modal" redirectPath={redirectPath} onClose={closeLogin} onCancel={handleCancel} />
      </div>
    </div>
  );

  return createPortal(overlay, getModalRoot());
}

